import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { PrivatePageLayout, PageHeader, PageTableCard } from '../../components/PrivatePage';
import { AppButtonLink } from '../../components/AppButton/AppButton';
import Toast from '../../components/Toast/Toast';
import { getBotPerformance } from '../../services/BotsService';
import { getBotTypeLabel } from './botUtils';
import './bot-performance.css';

function formatTimestamp(timestamp) {
    if (!timestamp) return '—';
    const date = new Date(Number(timestamp));
    return new Intl.DateTimeFormat('en-US', { dateStyle: 'short', timeStyle: 'short' }).format(date);
}

function formatNumber(value, precision = 4) {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) return '—';
    return parsed.toFixed(precision);
}

function formatProfit(value, quoteAsset = '') {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed)) return '—';
    const prefix = parsed > 0 ? '+' : '';
    return `${prefix}${parsed.toFixed(4)}${quoteAsset ? ` ${quoteAsset}` : ''}`;
}

function getProfitClass(value) {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed) || parsed === 0) return 'bot-performance-pnl--neutral';
    return parsed > 0 ? 'bot-performance-pnl--positive' : 'bot-performance-pnl--negative';
}

function TradeRow({ trade, quoteAsset }) {
    const isActive = trade.status === 'active';

    return (
        <tr>
            <td>
                <span className={`bot-performance-status bot-performance-status--${trade.status}`}>
                    {isActive ? 'Active' : 'Exited'}
                </span>
            </td>
            <td>{formatNumber(trade.quantity)}</td>
            <td>{formatNumber(trade.buyPrice)}</td>
            <td>{isActive ? '—' : formatNumber(trade.sellPrice)}</td>
            <td>{formatTimestamp(trade.buyTime)}</td>
            <td>{isActive ? '—' : formatTimestamp(trade.sellTime)}</td>
            <td className={getProfitClass(trade.profit)}>
                {isActive ? '—' : formatProfit(trade.profit, quoteAsset)}
            </td>
        </tr>
    );
}

function SummaryCard({ label, value, icon, tone = 'default' }) {
    return (
        <div className={`bot-performance-summary-card bot-performance-summary-card--${tone}`}>
            <span className="bot-performance-summary-icon" aria-hidden="true">
                <Icon icon={icon} width={22} height={22} />
            </span>
            <div>
                <p className="bot-performance-summary-label">{label}</p>
                <p className="bot-performance-summary-value">{value}</p>
            </div>
        </div>
    );
}

function BotPerformance() {
    const { sourceType, sourceId } = useParams();
    const [performance, setPerformance] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notification, setNotification] = useState(null);

    const loadPerformance = useCallback(() => {
        setIsLoading(true);
        getBotPerformance(sourceType, sourceId)
            .then((result) => setPerformance(result))
            .catch((err) => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            })
            .finally(() => setIsLoading(false));
    }, [sourceType, sourceId]);

    useEffect(() => {
        loadPerformance();
    }, [loadPerformance]);

    const bot = performance?.bot;
    const summary = performance?.summary || {};
    const quoteAsset = performance?.quoteAsset || '';
    const trades = performance?.trades || [];

    return (
        <PrivatePageLayout className="bot-performance-page">
            <PageHeader
                eyebrow="Bot performance"
                title={bot ? bot.name : 'Loading…'}
                subtitle={bot ? `${getBotTypeLabel(bot.type)} bot on ${bot.symbol}` : 'Trade history and profit/loss for this bot.'}
                actions={(
                    <AppButtonLink to="/bots" variant="secondary" size="md">
                        Back to bots
                    </AppButtonLink>
                )}
            />

            {notification ? <Toast type={notification.type} text={notification.text} /> : null}

            {isLoading ? (
                <div className="bot-performance-loading">Loading performance data…</div>
            ) : null}

            {!isLoading && performance ? (
                <>
                    <div className="bot-performance-summary-grid">
                        <SummaryCard
                            label="Active trades"
                            value={summary.activeTrades ?? 0}
                            icon="mdi:chart-timeline-variant"
                            tone="active"
                        />
                        <SummaryCard
                            label="Total trades"
                            value={summary.totalTrades ?? 0}
                            icon="mdi:swap-horizontal"
                        />
                        <SummaryCard
                            label="Exited trades"
                            value={summary.exitedTrades ?? 0}
                            icon="mdi:check-circle-outline"
                            tone="exited"
                        />
                        <SummaryCard
                            label="Total profit / loss"
                            value={formatProfit(summary.totalProfitLoss, quoteAsset)}
                            icon="mdi:finance"
                            tone={summary.totalProfitLoss > 0 ? 'profit' : summary.totalProfitLoss < 0 ? 'loss' : 'default'}
                        />
                    </div>

                    <p className="bot-performance-duration">
                        Tracking since {formatTimestamp(performance.startedAt)}
                        {summary.totalOrders ? ` · ${summary.totalOrders} filled orders` : ''}
                    </p>

                    <PageTableCard
                        icon="mdi:table"
                        title="Trades"
                        description="Active positions and completed round-trips with profit or loss per trade."
                    >
                        {trades.length === 0 ? (
                            <p className="bot-performance-empty">No filled trades yet for this bot.</p>
                        ) : (
                            <div className="table-wrapper">
                                <table className="table table-hover bot-performance-table">
                                    <thead>
                                        <tr>
                                            <th>Status</th>
                                            <th>Quantity</th>
                                            <th>Buy price</th>
                                            <th>Sell price</th>
                                            <th>Opened</th>
                                            <th>Closed</th>
                                            <th>Profit / loss</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {trades.map((trade) => (
                                            <TradeRow
                                                key={`${trade.status}-${trade.id}-${trade.buyOrderId}-${trade.sellOrderId || 'open'}`}
                                                trade={trade}
                                                quoteAsset={quoteAsset}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </PageTableCard>
                </>
            ) : null}
        </PrivatePageLayout>
    );
}

export default BotPerformance;